import { ArrowLeft, CheckCircle, Copy, Edit, Eye, Trash2 } from "lucide-react";
import React, { useEffect, useMemo, useRef } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import CustomerSuccessSection from "../components/CustomerSuccessSection";
import DeleteModal from "../components/DeleteModal";
import IntegrationWorkflow from "../components/IntegrationWorkflow";
import MarketingGrantSection from "../components/MarketingGrantSection";
import OnboardingWorkflow from "../components/OnboardingWorkflow";
import PartnerTasksSection from "../components/PartnerTasksSection";
import ProductionWhitelistForm from "../components/ProductionWhitelistForm";
import {
	Badge,
	Button,
	Card,
	Checkbox,
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "../components/ui";
import { useAuth } from "../context/AuthContext";
import { useWallets } from "../context/WalletContext";
import useConfirmDialog from "../hooks/useConfirmDialog";
import { useWalletDetailState } from "../hooks/useWalletDetailState";
import { useWalletTransactions } from "../hooks/useWalletTransactions";
import { INTEGRATION_STATUSES, ONBOARDING_STATUSES } from "../lib/constants";
import { getIntegrationBadgeVariant } from "../lib/statusBadge";
import { cn } from "../lib/utils";
import type {
	ChecklistItem,
	IntegrationStatus,
	OnboardingStatus,
	Wallet,
	WishlistStatus,
} from "../types";
import { notifications } from "../utils/notifications";

/** Derives the integration step to resume to when exiting On-Hold (stateless, no DB field). */
function deriveResumeStep(wallet: Wallet): IntegrationStatus {
	if (
		wallet.go_live_verification?.completed &&
		wallet.whitelisted_in_production?.completed
	) {
		return "Live";
	}
	if (wallet.testing_completed?.completed) {
		return "Ready to Go Live";
	}
	if (wallet.integration_completed?.completed) {
		return "Testing";
	}
	if (wallet.is_whitelisted) {
		return "Integrating";
	}
	return "New - Pre-Integration";
}

// Use shared constants — import only the subset needed for the status dropdowns
// (WalletDetail excludes On-Hold/Abandoned from the dropdown but allows them on read)
const ACTIVE_INTEGRATION_STATUSES = INTEGRATION_STATUSES.filter(
	(s) => s !== "On-Hold" && s !== "Abandoned",
);

interface WalletDetailProps {
	isPartnerView?: boolean;
}

const WalletDetail: React.FC<WalletDetailProps> = ({
	isPartnerView = false,
}) => {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const location = useLocation();
	const { isAdmin, user } = useAuth();
	const { confirm } = useConfirmDialog();
	const {
		getWallet,
		updateWallet,
		deleteWallet,
		updateChecklist,
		addTransaction,
		deleteTransaction,
		applyProposal,
		rejectProposal,
		isUpdating,
	} = useWallets();

	const [state, dispatch] = useWalletDetailState();
	const { modals, forms, ui } = state;

	// Adapter functions for setter props passed to child components
	const setSlackChannelInput = (v: string) =>
		dispatch({ type: "SET_FORM_FIELD", field: "slackChannel", value: v });
	const setShowAddTransaction = (v: boolean) =>
		dispatch({ type: "SET_MODAL", modal: "addTransaction", open: v });
	const setTransactionType = (v: "Deposit" | "Withdraw") =>
		dispatch({ type: "SET_FORM_FIELD", field: "transactionType", value: v });
	const setTransactionRef = (v: string) =>
		dispatch({ type: "SET_FORM_FIELD", field: "transactionRef", value: v });

	const baseWallet = id ? getWallet(id) : undefined;
	const { data: transactions = [] } = useWalletTransactions(id);
	const wallet = useMemo(
		() => (baseWallet ? { ...baseWallet, transactions } : undefined),
		[baseWallet, transactions],
	);

	const proposedChanges = wallet?.proposed_changes as
		| Record<string, unknown>
		| null
		| undefined;
	const hasProposedChanges =
		typeof proposedChanges === "object" &&
		proposedChanges !== null &&
		Object.keys(proposedChanges).length > 0;

	// Refs for scrolling to sections (must be before conditional return)
	const instructionsRef = React.useRef<HTMLDivElement>(null);
	const onboardingRef = React.useRef<HTMLDivElement>(null);

	// Guards to prevent auto-update effects from causing infinite loops when updateWallet
	// triggers query invalidation/refetch before the mutation completes
	const hasTriggeredOnboardingUpdateRef = useRef(false);
	const hasTriggeredTestingUpdateRef = useRef(false);
	const hasTriggeredLiveUpdateRef = useRef(false);

	// Primitive deps only to avoid re-running when wallet object reference changes (React 19 optimization).
	// biome-ignore lint/correctness/useExhaustiveDependencies: intentional primitive deps to reduce effect re-runs
	useEffect(() => {
		if (wallet) {
			dispatch({
				type: "SYNC_WALLET_INPUTS",
				agentId: wallet.agent_id || "",
				slackChannel: wallet.slack_channel || "",
			});
		}
	}, [wallet?.id, wallet?.agent_id, wallet?.slack_channel]);

	// Handle tab query parameter
	useEffect(() => {
		const searchParams = new URLSearchParams(location.search);
		const tab = searchParams.get("tab");
		if (tab === "onboarding") {
			dispatch({ type: "SET_ACTIVE_WORKSTREAM", value: "onboarding" });
		} else if (tab === "integration") {
			dispatch({ type: "SET_ACTIVE_WORKSTREAM", value: "integration" });
		}
	}, [location.search, dispatch]);

	// Check if all onboarding documents are complete (primitive deps for stable reference).
	// Partners only complete ndaSigned, kycFormCompleted, contractSigned;
	// admin-only Salesforce items are not required for partners.
	const onboardingComplete = isPartnerView
		? !!wallet?.nda_signed?.completed &&
			!!wallet?.kyc_form_completed?.completed &&
			!!wallet?.contract_signed?.completed
		: !!wallet?.salesforce_opportunity_created?.completed &&
			!!wallet?.nda_signed?.completed &&
			!!wallet?.kyc_form_completed?.completed &&
			!!wallet?.contract_signed?.completed &&
			!!wallet?.salesforce_case_created?.completed;

	// Reset auto-update guards when navigating to a different wallet
	// biome-ignore lint/correctness/useExhaustiveDependencies: reset only when wallet id changes
	useEffect(() => {
		hasTriggeredOnboardingUpdateRef.current = false;
		hasTriggeredLiveUpdateRef.current = false;
		hasTriggeredTestingUpdateRef.current = false;
	}, [wallet?.id]);

	// Auto-update onboarding status when all documents are complete.
	// Ref guard prevents infinite loop: updateWallet invalidates query → refetch → re-render → effect re-runs.
	// Skip auto-updates for existing partners (they were migrated with their current status).
	useEffect(() => {
		if (
			wallet?.id &&
			!wallet.is_existing_partner &&
			wallet.onboarding_status === "Not Started" &&
			onboardingComplete &&
			!hasTriggeredOnboardingUpdateRef.current
		) {
			hasTriggeredOnboardingUpdateRef.current = true;
			updateWallet(wallet.id, { onboarding_status: "Onboarding in Progress" });
		}
	}, [
		wallet?.id,
		wallet?.is_existing_partner,
		wallet?.onboarding_status,
		onboardingComplete,
		updateWallet,
	]); // eslint-disable-line react-hooks/exhaustive-deps

	// Auto-update integration status from Testing to Ready when testingCompleted.
	// Ref guard prevents infinite loop (same pattern as onboarding).
	// Skip auto-updates for existing partners.
	useEffect(() => {
		if (
			wallet?.id &&
			!wallet.is_existing_partner &&
			wallet.integration_status === "Testing" &&
			wallet.testing_completed?.completed &&
			!hasTriggeredTestingUpdateRef.current
		) {
			hasTriggeredTestingUpdateRef.current = true;
			updateWallet(wallet.id, { integration_status: "Ready to Go Live" });
		}
	}, [
		wallet?.id,
		wallet?.is_existing_partner,
		wallet?.integration_status,
		wallet?.testing_completed?.completed,
		updateWallet,
	]); // eslint-disable-line react-hooks/exhaustive-deps

	// Check if wallet should be marked as Live.
	// Ref guard prevents infinite loop (same pattern as onboarding).
	// Skip auto-updates for existing partners.
	useEffect(() => {
		if (
			wallet?.id &&
			!wallet.is_existing_partner &&
			wallet.integration_status === "Ready to Go Live" &&
			wallet.onboarding_status === "Onboarded" &&
			wallet.whitelisted_in_production?.completed &&
			wallet.go_live_verification?.completed &&
			!hasTriggeredLiveUpdateRef.current
		) {
			hasTriggeredLiveUpdateRef.current = true;
			updateWallet(wallet.id, { integration_status: "Live" });
		}
	}, [
		wallet?.id,
		wallet?.is_existing_partner,
		wallet?.integration_status,
		wallet?.onboarding_status,
		wallet?.whitelisted_in_production?.completed,
		wallet?.go_live_verification?.completed,
		updateWallet,
	]); // eslint-disable-line react-hooks/exhaustive-deps

	// Sync viewing step from wallet status (parent owns step; replaces child sync effects)
	// biome-ignore lint/correctness/useExhaustiveDependencies: intentional primitive deps to reduce effect re-runs
	useEffect(() => {
		if (wallet) {
			dispatch({
				type: "SET_VIEWING_INTEGRATION_STEP",
				step: wallet.integration_status,
			});
		}
	}, [wallet?.integration_status, dispatch]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: intentional primitive deps to reduce effect re-runs
	useEffect(() => {
		if (wallet) {
			dispatch({
				type: "SET_VIEWING_ONBOARDING_STEP",
				step: wallet.onboarding_status,
			});
		}
	}, [wallet?.onboarding_status, dispatch]);

	if (!wallet) {
		return (
			<div className="min-h-[calc(100vh-56px)] w-full bg-(--color-background) pt-20">
				<div className="mx-auto max-w-[1200px] px-4 py-12 md:px-6">
					<Card className="flex flex-col items-center gap-4 p-12 text-center rounded-xl bg-(--color-card) border-(--color-border)">
						<h2 className="font-semibold text-lg text-(--color-foreground)">
							Wallet not found
						</h2>
						<p className="text-(--color-muted-foreground) text-sm">
							The wallet you're looking for doesn't exist or couldn't be loaded.
						</p>
						<Button
							variant="outline"
							onClick={() => navigate("/")}
							className="rounded-xl border-(--color-border) hover:bg-(--color-muted)"
						>
							Back to Dashboard
						</Button>
					</Card>
				</div>
			</div>
		);
	}

	const showProductionWhitelistInfo = () => {
		dispatch({ type: "SET_MODAL", modal: "productionInfo", open: true });
	};

	const copyWhitelistInfo = () => {
		if (!wallet) return;

		let info = `Production Whitelisting Information:
Wallet Name: ${wallet.wallet_name}
Agent ID: ${wallet.agent_id || "Not set"}
Main Office ID: ${wallet.main_office_id || "Not set"}
Custodial Type: ${wallet.production_custodial_type || wallet.custodial_type}`;

		if (wallet.production_toml_file_domain) {
			info += `\nTOML File Domain: ${wallet.production_toml_file_domain}`;
		}
		if (wallet.production_home_domain || wallet.home_domain) {
			info += `\nHome Domain: ${wallet.production_home_domain || wallet.home_domain}`;
		}
		if (wallet.production_funding_address || wallet.funding_address) {
			info += `\nFunding Address: ${wallet.production_funding_address || wallet.funding_address}`;
		}
		if (wallet.production_deposit_address) {
			info += `\nDeposit Address: ${wallet.production_deposit_address}`;
		}
		if (wallet.production_auth_address) {
			info += `\nAuthentication Address: ${wallet.production_auth_address}`;
		}

		info += `\nExcluded Countries: ${wallet.excluded_countries && wallet.excluded_countries.length > 0 ? wallet.excluded_countries.join(", ") : "None"}`;

		navigator.clipboard.writeText(info).then(() => {
			notifications.success("Whitelisting info copied to clipboard!");
		});
	};

	const getCurrentUser = () => {
		return user?.name || user?.email || "System User";
	};

	const handleStatusChange = async (status: IntegrationStatus) => {
		// Only allow status change if wallet is whitelisted (except resume from On-Hold)
		if (!wallet.is_whitelisted && status !== "New - Pre-Integration") {
			return;
		}

		// On-Hold toggle: if already On-Hold, resume to derived step instead
		if (status === "On-Hold" && wallet.integration_status === "On-Hold") {
			const resumeStep = deriveResumeStep(wallet);
			updateWallet(wallet.id, { integration_status: resumeStep });
			dispatch({ type: "SET_ACTIVE_WORKSTREAM", value: "integration" });
			setTimeout(() => {
				instructionsRef.current?.scrollIntoView({
					behavior: "smooth",
					block: "start",
				});
			}, 100);
			return;
		}

		// Abandoned: permanent state, require confirmation
		if (status === "Abandoned") {
			const ok = await confirm({
				title: "Mark Integration as Abandoned",
				message:
					"This action cannot be undone. The integration will be permanently marked as abandoned. Continue?",
				confirmLabel: "Abandon",
				variant: "danger",
			});
			if (!ok) return;
		}

		updateWallet(wallet.id, { integration_status: status });

		// Switch to integration workstream and scroll
		dispatch({ type: "SET_ACTIVE_WORKSTREAM", value: "integration" });
		setTimeout(() => {
			instructionsRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}, 100);
	};

	const handleWhitelistCheck = () => {
		if (!wallet.is_whitelisted) {
			// Show dialog to select environments
			dispatch({ type: "SET_MODAL", modal: "whitelist", open: true });
		} else {
			// Uncheck whitelisting
			updateWallet(wallet.id, {
				is_whitelisted: false,
				whitelist_environments: [],
				integration_status: "New - Pre-Integration",
			});
		}
	};

	const handleWhitelistConfirm = () => {
		if (forms.whitelistEnvs.length === 0) {
			notifications.error(
				"Please select at least one environment (Sandbox or Preview)",
			);
			return;
		}

		updateWallet(wallet.id, {
			is_whitelisted: true,
			whitelist_environments: forms.whitelistEnvs as WishlistStatus[],
			integration_status: "Integrating",
		});
		dispatch({ type: "RESET_WHITELIST_FORM" });
		dispatch({ type: "SET_MODAL", modal: "whitelist", open: false });
		notifications.success("Whitelist confirmed successfully!");
	};

	const handleOnboardingStatusChange = (status: OnboardingStatus) => {
		updateWallet(wallet.id, { onboarding_status: status });

		// Switch to onboarding workstream and scroll
		dispatch({ type: "SET_ACTIVE_WORKSTREAM", value: "onboarding" });
		setTimeout(() => {
			onboardingRef.current?.scrollIntoView({
				behavior: "smooth",
				block: "start",
			});
		}, 100);
	};

	const handleChecklistToggle = (checklistKey: string, item: unknown) => {
		const typedItem = item as ChecklistItem;
		const newCompleted = !typedItem.completed;

		// Salesforce Case is handled by the inline form in OnboardingWorkflow
		// Partner Activation is handled by the modal in OnboardingWorkflow
		// so we just update the checklist here

		updateChecklist(wallet.id, checklistKey as keyof Wallet, {
			completed: newCompleted,
			completed_by: newCompleted ? getCurrentUser() : undefined,
			completed_at: newCompleted ? new Date().toISOString() : undefined,
		});

		// Auto-status updates based on checklist completion

		// Integration Completed -> Testing
		if (checklistKey === "integration_completed" && newCompleted) {
			updateWallet(wallet.id, { integration_status: "Testing" });
			notifications.success(
				"Integration marked complete. Status moved to Testing.",
			);
		}

		// Testing Completed -> Ready to Go Live
		if (checklistKey === "testing_completed" && newCompleted) {
			updateWallet(wallet.id, { integration_status: "Ready to Go Live" });
			notifications.success(
				"Testing marked complete. Status moved to Ready to Go Live.",
			);
		}

		// Partner Activated -> Onboarded
		if (checklistKey === "partner_activated" && newCompleted) {
			updateWallet(wallet.id, { onboarding_status: "Onboarded" });
			notifications.success(
				"Partner activated. Onboarding status set to Onboarded.",
			);
		}
	};

	const handleAddTransaction = () => {
		if (!forms.transactionRef.trim()) return;

		addTransaction(wallet.id, {
			id: `transaction-${Date.now()}`,
			type: forms.transactionType,
			referenceNumber: forms.transactionRef,
			timestamp: new Date().toISOString(),
		});

		// Change status to Live after first transaction
		if (wallet.transactions.length === 0) {
			handleStatusChange("Live");
		}

		dispatch({ type: "RESET_TRANSACTION_FORM" });
	};

	const handleSlackChannelSave = () => {
		if (forms.slackChannel.trim()) {
			updateWallet(wallet.id, {
				slack_channel: forms.slackChannel,
				slack_channel_created: {
					...wallet.slack_channel_created,
					completed: true,
				},
			});
			notifications.success("Slack channel saved successfully!");
		}
	};

	const handleDeleteSlackChannel = () => {
		updateWallet(wallet.id, {
			slack_channel: "",
			slack_channel_created: {
				...wallet.slack_channel_created,
				completed: false,
			},
		});
		dispatch({ type: "SET_FORM_FIELD", field: "slackChannel", value: "" });
	};

	const handleDeleteSalesforceCase = () => {
		updateWallet(wallet.id, {
			salesforce_case_number: "",
			salesforce_case_created: {
				...wallet.salesforce_case_created,
				completed: false,
			},
		});
	};

	const handleSubtaskToggle = (
		checklistKey: string,
		field: "sent" | "received",
	) => {
		const item = wallet[checklistKey as keyof Wallet] as ChecklistItem;
		if (!item.subtasks) return;

		const newValue = !item.subtasks[field];
		const updatedSubtasks = {
			...item.subtasks,
			[field]: newValue,
			[`${field}At`]: newValue ? new Date().toISOString() : undefined,
			[`${field}By`]: newValue ? getCurrentUser() : undefined,
		};

		// Check if both sent and received are now checked
		const bothChecked =
			(field === "sent" ? newValue : item.subtasks.sent) &&
			(field === "received" ? newValue : item.subtasks.received);

		// Auto-check the main task if both subtasks are checked
		updateChecklist(wallet.id, checklistKey as keyof Wallet, {
			subtasks: updatedSubtasks,
			completed: bothChecked ? true : item.completed,
			completed_by:
				bothChecked && !item.completed ? getCurrentUser() : item.completedBy,
			completed_at:
				bothChecked && !item.completed
					? new Date().toISOString()
					: item.completedAt,
		});
	};

	const handleDocumentUpload = async (checklistKey: string, file: File) => {
		// In a real app, this would upload to a server
		// For now, we'll store it as a base64 data URL in localStorage
		const reader = new FileReader();
		reader.onloadend = () => {
			const item = wallet[checklistKey as keyof Wallet] as ChecklistItem;
			if (!item.subtasks) {
				return;
			}
			const updatedSubtasks = {
				...item.subtasks,
				documentUrl: reader.result as string,
				documentName: file.name,
			};

			updateChecklist(wallet.id, checklistKey as keyof Wallet, {
				subtasks: updatedSubtasks,
			});
			notifications.success(`Document "${file.name}" uploaded successfully!`);
		};
		reader.readAsDataURL(file);
	};

	const _canWhitelistProduction = () => {
		return (
			wallet.testing_completed?.completed &&
			wallet.onboarding_status === "Onboarded"
		);
	};

	const handleDeleteWallet = async () => {
		await deleteWallet(wallet.id);
		navigate("/");
	};

	// Shared class constants
	const infoLabel =
		"text-xs font-medium uppercase tracking-wide text-(--color-muted-foreground)";
	const infoValue = "text-sm font-medium text-(--color-foreground)";
	const infoItem = "flex flex-col gap-1";
	const infoGrid = "grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3";
	const codeBlock =
		"block break-all rounded-xl bg-(--color-muted) p-3 text-sm text-(--color-foreground) font-mono border border-(--color-border)";

	return (
		<div className="min-h-[calc(100vh-56px)] w-full bg-(--color-background) pt-20">
			<div className="mx-auto max-w-[1200px] px-4 py-6 md:px-6">
				{/* Compact Header for Both Views */}
				<div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
					<div className="flex flex-wrap items-center gap-2">
						<h1 className="font-bold text-(--color-foreground) text-2xl md:text-3xl">
							{wallet.wallet_name}
						</h1>
						<span className="text-(--color-muted-foreground)">•</span>
						<p className="text-(--color-muted-foreground) text-base">
							{wallet.company_name}
						</p>
					</div>
					<div className="flex flex-wrap items-center gap-2">
						{isPartnerView && isAdmin && (
							<Button
								asChild
								variant="ghost"
								size="sm"
								className="rounded-xl hover:bg-(--color-muted)"
							>
								<Link to={`/wallet/${wallet.id}`}>
									<ArrowLeft size={14} />
									Back to Admin View
								</Link>
							</Button>
						)}
						{!isPartnerView && (
							<>
								<Button
									asChild
									variant="ghost"
									size="sm"
									className="rounded-xl hover:bg-(--color-muted)"
								>
									<Link to={`/wallet/${wallet.id}/partner`}>
										<Eye size={14} />
										Partner View
									</Link>
								</Button>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									onClick={() => navigate(`/wallet/${wallet.id}/edit`)}
									className="rounded-xl hover:bg-(--color-muted)"
								>
									<Edit size={14} />
									Edit
								</Button>
								<Button
									type="button"
									variant="destructive"
									size="sm"
									onClick={() =>
										dispatch({ type: "SET_MODAL", modal: "delete", open: true })
									}
									className="rounded-xl bg-(--color-cta-primary) hover:bg-(--color-cta-primary-hover)"
								>
									<Trash2 size={14} />
									Delete
								</Button>
							</>
						)}
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={() =>
								dispatch({ type: "TOGGLE_MODAL", modal: "moreInfo" })
							}
							className="rounded-xl border-(--color-border) hover:bg-(--color-muted)"
						>
							{modals.moreInfo ? "Hide Details" : "Show Details"}
						</Button>
					</div>
				</div>

				{/* Partner proposed changes – admin Approve / Reject */}
				{!isPartnerView && isAdmin && hasProposedChanges && (
					<div className="mb-4 rounded-xl border-2 border-[#F59E0B] bg-[#F59E0B]/10 dark:bg-[#F59E0B]/5 p-4">
						<div className="flex flex-wrap items-center justify-between gap-3">
							<div>
								<strong className="text-(--color-foreground)">
									Pending partner changes
								</strong>
								{wallet.proposed_changes_at && (
									<span className="ml-2 text-(--color-muted-foreground)">
										Submitted{" "}
										{new Date(wallet.proposed_changes_at).toLocaleString()}
									</span>
								)}
							</div>
							<div className="flex gap-2">
								<Button
									type="button"
									size="sm"
									disabled={ui.proposalActionLoading}
									onClick={async () => {
										if (!wallet?.id) return;
										dispatch({ type: "SET_PROPOSAL_LOADING", value: true });
										try {
											await applyProposal(wallet.id);
										} finally {
											dispatch({ type: "SET_PROPOSAL_LOADING", value: false });
										}
									}}
									className="rounded-xl bg-(--color-cta-primary) hover:bg-(--color-cta-primary-hover)"
								>
									{ui.proposalActionLoading ? "Applying…" : "Approve"}
								</Button>
								<Button
									type="button"
									variant="outline"
									size="sm"
									disabled={ui.proposalActionLoading}
									onClick={async () => {
										if (!wallet?.id) return;
										dispatch({ type: "SET_PROPOSAL_LOADING", value: true });
										try {
											await rejectProposal(wallet.id);
										} finally {
											dispatch({ type: "SET_PROPOSAL_LOADING", value: false });
										}
									}}
									className="rounded-xl border-(--color-border) hover:bg-(--color-muted)"
								>
									Reject
								</Button>
							</div>
						</div>
					</div>
				)}

				{/* Wallet Details - Unified for Both Views */}
				{isPartnerView ? (
					<>
						{/* Expanded Info Grid for Partner */}
						{modals.moreInfo && (
							<Card className="mb-4 p-5 rounded-xl bg-(--color-card) border-(--color-border)">
								<div className={infoGrid}>
									<div className={infoItem}>
										<span className={infoLabel}>Custodial Type</span>
										<span className={infoValue}>{wallet.custodial_type}</span>
									</div>
									<div className={infoItem}>
										<span className={infoLabel}>Chain</span>
										<span className={infoValue}>{wallet.chain}</span>
									</div>
									<div className={infoItem}>
										<span className={infoLabel}>Asset</span>
										<span className={infoValue}>{wallet.asset}</span>
									</div>
									<div className={infoItem}>
										<span className={infoLabel}>Products</span>
										<span className={infoValue}>
											{(wallet.products ?? []).join(", ")}
										</span>
									</div>
								</div>
							</Card>
						)}
					</>
				) : (
					<>
						{/* Expanded Info Grid for Admin */}
						{modals.moreInfo && (
							<Card className="mb-4 p-5 rounded-xl bg-(--color-card) border-(--color-border)">
								<div className={infoGrid}>
									<div className={infoItem}>
										<span className={infoLabel}>Custodial Type</span>
										<span className={infoValue}>{wallet.custodial_type}</span>
									</div>
									<div className={infoItem}>
										<span className={infoLabel}>Chain</span>
										<span className={infoValue}>{wallet.chain}</span>
									</div>
									<div className={infoItem}>
										<span className={infoLabel}>Asset</span>
										<span className={infoValue}>{wallet.asset}</span>
									</div>
									<div className={infoItem}>
										<span className={infoLabel}>Products</span>
										<span className={infoValue}>
											{(wallet.products ?? []).join(", ")}
										</span>
									</div>
								</div>
								{/* Status badge in expanded view */}
								<div className="mt-4 border-(--color-border) border-t pt-4">
									<span className={cn(infoLabel, "mb-2 block")}>
										Integration Status
									</span>
									<Badge
										variant={getIntegrationBadgeVariant(
											wallet.integration_status,
										)}
									>
										{wallet.integration_status}
									</Badge>
								</div>
							</Card>
						)}
					</>
				)}

				{/* Main Contact - Hidden by default */}
				{modals.moreInfo && wallet.main_contact_name && (
					<Card className="mb-4 p-5 rounded-xl bg-(--color-card) border-(--color-border)">
						<h3 className="mb-4 font-semibold text-(--color-foreground) text-lg">
							Main Contact
						</h3>
						<div className={infoGrid}>
							{wallet.main_contact_name && (
								<div className={infoItem}>
									<span className={infoLabel}>Name</span>
									<span className={infoValue}>{wallet.main_contact_name}</span>
								</div>
							)}
							{wallet.main_contact_email && (
								<div className={infoItem}>
									<span className={infoLabel}>Email</span>
									<span className={infoValue}>{wallet.main_contact_email}</span>
								</div>
							)}
							{wallet.main_contact_phone && (
								<div className={infoItem}>
									<span className={infoLabel}>Phone</span>
									<span className={infoValue}>{wallet.main_contact_phone}</span>
								</div>
							)}
							{wallet.main_contact_role && (
								<div className={infoItem}>
									<span className={infoLabel}>Role</span>
									<span className={infoValue}>{wallet.main_contact_role}</span>
								</div>
							)}
							{wallet.main_contact_email && (
								<div className={infoItem}>
									<span className={infoLabel}>Primary Contact Email</span>
									<span className={infoValue}>{wallet.main_contact_email}</span>
								</div>
							)}
						</div>
					</Card>
				)}

				{/* Technical Information - Hidden by default */}
				{modals.moreInfo &&
					(wallet.slack_channel ||
						wallet.home_domain ||
						wallet.funding_address) && (
						<Card className="mb-4 p-5 rounded-xl bg-(--color-card) border-(--color-border)">
							<h3 className="mb-4 font-semibold text-(--color-foreground) text-lg">
								Technical Information
							</h3>
							<div className={infoGrid}>
								{wallet.slack_channel && (
									<div className={infoItem}>
										<span className={infoLabel}>Slack Channel</span>
										<span className={infoValue}>{wallet.slack_channel}</span>
									</div>
								)}
								{wallet.home_domain && (
									<div className={infoItem}>
										<span className={infoLabel}>Home Domain</span>
										<span className={infoValue}>{wallet.home_domain}</span>
									</div>
								)}
							</div>
							{wallet.funding_address && (
								<div className="mt-4">
									<span className={cn(infoLabel, "mb-2 block")}>
										Funding Address
									</span>
									<code className={codeBlock}>{wallet.funding_address}</code>
								</div>
							)}
						</Card>
					)}

				{/* Additional Information - Hidden by default */}
				{modals.moreInfo &&
					(wallet.conducts_kyc !== undefined ||
						wallet.mau ||
						(wallet as Record<string, unknown>).licenses ||
						wallet.country_of_incorporation ||
						wallet.priority_markets ||
						(wallet.whitelist_environments?.length ?? 0) > 0 ||
						wallet.agent_id ||
						wallet.main_office_id) && (
						<Card className="mb-4 p-5 rounded-xl bg-(--color-card) border-(--color-border)">
							<h3 className="mb-4 font-semibold text-(--color-foreground) text-lg">
								Additional Information
							</h3>

							<div className={infoGrid}>
								{wallet.conducts_kyc !== undefined && (
									<div className={infoItem}>
										<span className={infoLabel}>Conducts KYC</span>
										<span className={infoValue}>
											{wallet.conducts_kyc ? "Yes" : "No"}
										</span>
									</div>
								)}
								{wallet.mau && (
									<div className={infoItem}>
										<span className={infoLabel}>MAU Range</span>
										<span className={infoValue}>{wallet.mau}</span>
									</div>
								)}
								{wallet.country_of_incorporation && (
									<div className={infoItem}>
										<span className={infoLabel}>Country of Incorporation</span>
										<span className={infoValue}>
											{wallet.country_of_incorporation}
										</span>
									</div>
								)}
								{wallet.agent_id && (
									<div className={infoItem}>
										<span className={infoLabel}>Agent ID</span>
										<span className={infoValue}>{wallet.agent_id}</span>
									</div>
								)}
								{wallet.main_office_id && (
									<div className={infoItem}>
										<span className={infoLabel}>Main Office ID</span>
										<span className={infoValue}>{wallet.main_office_id}</span>
									</div>
								)}
							</div>

							{!!(wallet as Record<string, unknown>).licenses && (
								<div className="mt-4">
									<span className={cn(infoLabel, "mb-2 block")}>Licenses</span>
									<p className="m-0 text-(--color-foreground) leading-relaxed text-sm">
										{String((wallet as Record<string, unknown>).licenses)}
									</p>
								</div>
							)}

							{wallet.priority_markets &&
								wallet.priority_markets.length > 0 && (
									<div className="mt-4">
										<span className={cn(infoLabel, "mb-2 block")}>
											Priority Markets
										</span>
										<div className="mt-2 flex flex-wrap gap-2">
											{wallet.priority_markets.map((market) => (
												<Badge key={market} variant="secondary">
													{market}
												</Badge>
											))}
										</div>
									</div>
								)}

							{(wallet.whitelist_environments?.length ?? 0) > 0 && (
								<div className="mt-4">
									<span className={cn(infoLabel, "mb-2 block")}>
										Whitelist Status
									</span>
									<div className="flex flex-wrap gap-2">
										{(wallet.whitelist_environments ?? []).map((status) => (
											<Badge key={status} variant="info">
												{status}
											</Badge>
										))}
									</div>
								</div>
							)}
						</Card>
					)}

				{/* Production Information - Hidden by default */}
				{modals.moreInfo &&
					(wallet.production_custodial_type ||
						wallet.production_toml_file_domain ||
						wallet.production_home_domain ||
						wallet.production_funding_address ||
						wallet.production_deposit_address ||
						wallet.production_auth_address ||
						(wallet.excluded_countries &&
							wallet.excluded_countries.length > 0)) && (
						<Card className="mb-4 p-5 rounded-xl bg-(--color-card) border-(--color-border)">
							<div className="mb-4 flex items-center justify-between">
								<h3 className="font-semibold text-(--color-foreground) text-lg">
									Production Information
								</h3>
								{!isPartnerView && (
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => {
											dispatch({
												type: "SET_ACTIVE_WORKSTREAM",
												value: "integration",
											});
											setTimeout(() => {
												instructionsRef.current?.scrollIntoView({
													behavior: "smooth",
													block: "start",
												});
											}, 100);
										}}
										className="rounded-xl border-(--color-border) hover:bg-(--color-muted)"
									>
										Edit in Integration Step 4 →
									</Button>
								)}
							</div>

							<div className={infoGrid}>
								{wallet.production_custodial_type && (
									<div className={infoItem}>
										<span className={infoLabel}>Custodial Type</span>
										<span className={infoValue}>
											{wallet.production_custodial_type}
										</span>
									</div>
								)}
								{wallet.production_toml_file_domain && (
									<div className={infoItem}>
										<span className={infoLabel}>TOML File Domain</span>
										<span className={infoValue}>
											{wallet.production_toml_file_domain}
										</span>
									</div>
								)}
								{wallet.production_home_domain && (
									<div className={infoItem}>
										<span className={infoLabel}>Home Domain</span>
										<span className={infoValue}>
											{wallet.production_home_domain}
										</span>
									</div>
								)}
							</div>

							{wallet.production_funding_address && (
								<div className="mt-4">
									<span className={cn(infoLabel, "mb-2 block")}>
										Funding Address
									</span>
									<code className={codeBlock}>
										{wallet.production_funding_address}
									</code>
								</div>
							)}

							{wallet.production_deposit_address && (
								<div className="mt-4">
									<span className={cn(infoLabel, "mb-2 block")}>
										Deposit Address
									</span>
									<code className={codeBlock}>
										{wallet.production_deposit_address}
									</code>
								</div>
							)}

							{wallet.production_auth_address && (
								<div className="mt-4">
									<span className={cn(infoLabel, "mb-2 block")}>
										Authentication Address
									</span>
									<code className={codeBlock}>
										{wallet.production_auth_address}
									</code>
								</div>
							)}

							{wallet.excluded_countries &&
								wallet.excluded_countries.length > 0 && (
									<div className="mt-4">
										<span className={cn(infoLabel, "mb-2 block")}>
											Excluded Countries
										</span>
										<div className="mt-4 flex flex-wrap gap-2">
											{wallet.excluded_countries.map((country) => (
												<Badge key={country} variant="destructive">
													{country}
												</Badge>
											))}
										</div>
									</div>
								)}
						</Card>
					)}

				{/* Show placeholder if no production info */}
				{modals.moreInfo &&
					!wallet.production_custodial_type &&
					!wallet.production_toml_file_domain &&
					!wallet.production_home_domain &&
					!wallet.production_funding_address &&
					!wallet.production_deposit_address &&
					!wallet.production_auth_address &&
					(!wallet.excluded_countries ||
						wallet.excluded_countries.length === 0) && (
						<Card className="mb-4 p-6">
							<div className="mb-5 flex items-center justify-between">
								<h3 className="font-semibold text-(--color-foreground) text-lg">
									Production Information
								</h3>
								{!isPartnerView && (
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => {
											dispatch({
												type: "SET_ACTIVE_WORKSTREAM",
												value: "integration",
											});
											setTimeout(() => {
												instructionsRef.current?.scrollIntoView({
													behavior: "smooth",
													block: "start",
												});
											}, 100);
										}}
									>
										Edit in Integration Step 4 →
									</Button>
								)}
							</div>
							<p className="m-0 text-(--color-muted-foreground) text-sm italic">
								No production information added yet. Go to Integration Step 4 to
								add production details.
							</p>
						</Card>
					)}

				{/* Transactions - Read Only */}
				{modals.moreInfo &&
					wallet.transactions &&
					wallet.transactions.length > 0 && (
						<Card className="mb-4 p-6">
							<div className="mb-5 flex items-center justify-between">
								<h3 className="font-semibold text-(--color-foreground) text-lg">
									Transactions
								</h3>
								{!isPartnerView && (
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => {
											dispatch({
												type: "SET_ACTIVE_WORKSTREAM",
												value: "integration",
											});
											setTimeout(() => {
												instructionsRef.current?.scrollIntoView({
													behavior: "smooth",
													block: "start",
												});
											}, 100);
										}}
									>
										Manage in Integration Workflow →
									</Button>
								)}
							</div>

							<div className="flex flex-col gap-2">
								{wallet.transactions.slice(0, 5).map((transaction) => (
									<div
										key={transaction.id}
										className="flex items-center justify-between rounded-(--radius-sm) border border-(--color-border) bg-(--color-muted) p-4"
									>
										<div>
											<span
												className={cn(
													"font-semibold text-[0.9375rem]",
													transaction.type === "Deposit"
														? "text-(--color-success-600)"
														: "text-(--color-error-600)",
												)}
											>
												{transaction.type}
											</span>
											<span className="ml-4 text-(--color-muted-foreground) text-sm">
												Ref: {transaction.referenceNumber}
											</span>
										</div>
										<span className="text-(--color-muted-foreground) text-sm">
											{new Date(transaction.timestamp).toLocaleDateString(
												"en-US",
												{
													month: "short",
													day: "numeric",
													year: "numeric",
												},
											)}
										</span>
									</div>
								))}
							</div>

							{wallet.transactions.length > 5 && (
								<p className="mt-3 mb-0 text-center text-(--color-muted-foreground) text-sm">
									Showing 5 of {wallet.transactions.length} transactions
								</p>
							)}
						</Card>
					)}

				{/* Fee Structures - Read Only */}
				{modals.moreInfo &&
					wallet.fee_structures &&
					wallet.fee_structures.length > 0 && (
						<Card className="mb-4 p-6">
							<div className="mb-5 flex items-center justify-between">
								<h3 className="font-semibold text-(--color-foreground) text-lg">
									Fee Structures
								</h3>
								{!isPartnerView && (
									<Button
										type="button"
										variant="outline"
										size="sm"
										onClick={() => {
											dispatch({
												type: "SET_ACTIVE_WORKSTREAM",
												value: "onboarding",
											});
											setTimeout(() => {
												onboardingRef.current?.scrollIntoView({
													behavior: "smooth",
													block: "start",
												});
											}, 100);
										}}
									>
										Edit in Onboarding Step 2 →
									</Button>
								)}
							</div>

							<div className="overflow-x-auto rounded-(--radius-sm) border border-(--color-border)">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead>Start Range</TableHead>
											<TableHead>End Range</TableHead>
											<TableHead>Fee</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{wallet.fee_structures.map((fee, index) => (
											<TableRow
												key={`fee-${fee.startRange}-${fee.endRange}-${fee.fee}-${fee.feeType || "flat"}-${index}`}
											>
												<TableCell>
													${(fee.startRange ?? 0).toLocaleString()}
												</TableCell>
												<TableCell>
													${(fee.endRange ?? 0).toLocaleString()}
												</TableCell>
												<TableCell className="font-semibold text-(--color-success-600)">
													${fee.fee}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</div>
						</Card>
					)}

				{/* Salesforce Case - Read Only */}
				{modals.moreInfo && wallet.salesforce_case_number && (
					<Card className="mb-4 p-5 rounded-xl bg-(--color-card) border-(--color-border)">
						<div className="mb-4 flex items-center justify-between">
							<h3 className="font-semibold text-(--color-foreground) text-lg">
								Salesforce Case
							</h3>
							{!isPartnerView && (
								<Button
									type="button"
									variant="outline"
									size="sm"
									onClick={() => {
										dispatch({
											type: "SET_ACTIVE_WORKSTREAM",
											value: "onboarding",
										});
										setTimeout(() => {
											onboardingRef.current?.scrollIntoView({
												behavior: "smooth",
												block: "start",
											});
										}, 100);
									}}
									className="rounded-xl border-(--color-border) hover:bg-(--color-muted)"
								>
									Edit in Onboarding Step 1 →
								</Button>
							)}
						</div>

						<div className="rounded-xl border border-(--color-border) bg-(--color-muted) p-4">
							<div className={infoItem}>
								<span className={infoLabel}>Case Number</span>
								<span className={cn(infoValue, "font-mono font-semibold")}>
									{wallet.salesforce_case_number}
								</span>
							</div>
						</div>
					</Card>
				)}

				<ProductionWhitelistForm
					wallet={wallet}
					isOpen={modals.productionEdit}
					onClose={() =>
						dispatch({
							type: "SET_MODAL",
							modal: "productionEdit",
							open: false,
						})
					}
					onSave={(data) => updateWallet(wallet.id, data)}
				/>

				{/* Partner Tracker Sections (admin & stellar_admin only) */}
				{!isPartnerView && (
					<>
						<CustomerSuccessSection
							data={wallet.customer_success}
							readOnly={!isAdmin}
							onUpdate={
								isAdmin
									? (data) =>
											updateWallet(wallet.id, {
												customer_success: data,
											})
									: undefined
							}
						/>
						<MarketingGrantSection
							data={wallet.marketing_grant}
							readOnly={!isAdmin}
							onUpdate={
								isAdmin
									? (data) =>
											updateWallet(wallet.id, {
												marketing_grant: data,
											})
									: undefined
							}
						/>
						<PartnerTasksSection
							tasks={wallet.partner_tasks}
							readOnly={!isAdmin}
							onUpdate={
								isAdmin
									? (tasks) => updateWallet(wallet.id, { partner_tasks: tasks })
									: undefined
							}
						/>
					</>
				)}

				{/* Status tracking moved into each workstream tab */}

				{/* Workstream Toggle with Status Indicator */}
				<div className="mb-4 flex flex-wrap items-center justify-between gap-3 border-(--color-border) border-b pb-3">
					<div className="flex gap-1">
						<button
							type="button"
							className={cn(
								"rounded-xl px-4 py-2 font-medium text-sm transition-all",
								ui.activeWorkstream === "integration"
									? "bg-(--color-accent-muted) font-semibold text-(--color-accent)"
									: "text-(--color-muted-foreground) hover:bg-(--color-muted)",
							)}
							onClick={() =>
								dispatch({
									type: "SET_ACTIVE_WORKSTREAM",
									value: "integration",
								})
							}
						>
							Integration
						</button>
						<button
							type="button"
							className={cn(
								"rounded-xl px-4 py-2 font-medium text-sm transition-all",
								ui.activeWorkstream === "onboarding"
									? "bg-(--color-accent-muted) font-semibold text-(--color-accent)"
									: "text-(--color-muted-foreground) hover:bg-(--color-muted)",
							)}
							onClick={() =>
								dispatch({ type: "SET_ACTIVE_WORKSTREAM", value: "onboarding" })
							}
						>
							Onboarding
						</button>
					</div>

					{/* Compact Status Indicator */}
					<div className="flex items-center gap-3">
						<span className="text-(--color-muted-foreground) text-sm font-medium">
							{ui.activeWorkstream === "integration"
								? wallet.integration_status
								: wallet.onboarding_status}
						</span>
						<div className="flex items-center">
							{ui.activeWorkstream === "integration"
								? ACTIVE_INTEGRATION_STATUSES.map((status, index) => {
										// When On-Hold/Abandoned, use derived resume step so circles stay navigable
										const effectiveStatus =
											wallet.integration_status === "On-Hold" ||
											wallet.integration_status === "Abandoned"
												? deriveResumeStep(wallet)
												: wallet.integration_status;
										const currentIndex = ACTIVE_INTEGRATION_STATUSES.indexOf(
											effectiveStatus as (typeof ACTIVE_INTEGRATION_STATUSES)[number],
										);
										const isActive = effectiveStatus === status;
										const isCompleted = currentIndex > index;
										const isClickable = isCompleted || isActive;
										const isViewing =
											(ui.viewingIntegrationStep ||
												wallet.integration_status) === status;

										return (
											<React.Fragment key={status}>
												{index > 0 && (
													<div
														className={cn(
															"h-0.5 w-4 shrink-0 bg-(--color-border)",
															isCompleted && "bg-[#00E6C3]",
														)}
													/>
												)}
												<button
													type="button"
													className={cn(
														"flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 font-semibold text-xs transition-all",
														isActive &&
															"border-(--color-accent) bg-(--color-cta-primary) text-white",
														isCompleted &&
															"border-[#00E6C3] bg-[#00E6C3] text-white",
														isCompleted &&
															isClickable &&
															"cursor-pointer hover:scale-110",
														!isActive &&
															!isCompleted &&
															"border-(--color-border) bg-(--color-card) text-(--color-muted-foreground)",
														isViewing &&
															"ring-(--color-accent) ring-2 ring-offset-2",
														!isClickable && "cursor-not-allowed opacity-50",
													)}
													title={status}
													onClick={() => {
														if (isClickable) {
															dispatch({
																type: "SET_VIEWING_INTEGRATION_STEP",
																step: status as IntegrationStatus,
															});
														}
													}}
													disabled={!isClickable}
												>
													{isCompleted ? "✓" : index + 1}
												</button>
											</React.Fragment>
										);
									})
								: ONBOARDING_STATUSES.map((status, index) => {
										const currentIndex = ONBOARDING_STATUSES.indexOf(
											wallet.onboarding_status,
										);
										const isActive = wallet.onboarding_status === status;
										const isCompleted = currentIndex > index;
										const isClickable = isCompleted || isActive;
										const isViewing =
											(ui.viewingOnboardingStep || wallet.onboarding_status) ===
											status;

										return (
											<React.Fragment key={status}>
												{index > 0 && (
													<div
														className={cn(
															"h-0.5 w-4 shrink-0 bg-(--color-border)",
															isCompleted && "bg-[#00E6C3]",
														)}
													/>
												)}
												<button
													type="button"
													className={cn(
														"flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2 font-semibold text-xs transition-all",
														isActive &&
															"border-(--color-accent) bg-(--color-cta-primary) text-white",
														isCompleted &&
															"border-[#00E6C3] bg-[#00E6C3] text-white",
														isCompleted &&
															isClickable &&
															"cursor-pointer hover:scale-110",
														!isActive &&
															!isCompleted &&
															"border-(--color-border) bg-(--color-card) text-(--color-muted-foreground)",
														isViewing &&
															"ring-(--color-accent) ring-2 ring-offset-2",
														!isClickable && "cursor-not-allowed opacity-50",
													)}
													title={status}
													onClick={() => {
														if (isClickable) {
															dispatch({
																type: "SET_VIEWING_ONBOARDING_STEP",
																step: status as OnboardingStatus,
															});
														}
													}}
													disabled={!isClickable}
												>
													{isCompleted ? "✓" : index + 1}
												</button>
											</React.Fragment>
										);
									})}
						</div>
					</div>
				</div>

				{/* Integration Workstream */}
				{ui.activeWorkstream === "integration" && (
					<Card
						className="mb-4 p-6 rounded-xl bg-(--color-card) border-(--color-border)"
						ref={instructionsRef}
					>
						<IntegrationWorkflow
							wallet={wallet}
							isUpdating={isUpdating}
							onShowSuccess={(msg) => notifications.success(msg)}
							slackChannel={{
								value: forms.slackChannel,
								onChange: setSlackChannelInput,
								onSave: handleSlackChannelSave,
								onDelete: handleDeleteSlackChannel,
							}}
							transaction={{
								type: forms.transactionType,
								ref: forms.transactionRef,
								showAdd: modals.addTransaction,
								setType: setTransactionType,
								setRef: setTransactionRef,
								setShowAdd: setShowAddTransaction,
								onAdd: handleAddTransaction,
								onDelete: deleteTransaction,
							}}
							checklist={{
								onToggle: handleChecklistToggle,
								onWhitelistCheck: handleWhitelistCheck,
								onStatusChange: handleStatusChange,
								showProductionInfo: showProductionWhitelistInfo,
								updateWallet,
							}}
							isPartnerView={isPartnerView}
							viewingStep={ui.viewingIntegrationStep}
						/>
					</Card>
				)}

				{/* Onboarding Workstream */}
				{ui.activeWorkstream === "onboarding" && (
					<Card
						className="mb-4 p-6 rounded-xl bg-(--color-card) border-(--color-border)"
						ref={onboardingRef}
					>
						<OnboardingWorkflow
							wallet={wallet}
							isUpdating={isUpdating}
							handleChecklistToggle={handleChecklistToggle}
							handleSubtaskToggle={handleSubtaskToggle}
							handleDocumentUpload={handleDocumentUpload}
							updateWallet={updateWallet}
							handleOnboardingStatusChange={handleOnboardingStatusChange}
							handleDeleteSalesforceCase={handleDeleteSalesforceCase}
							isPartnerView={isPartnerView}
							viewingStep={ui.viewingOnboardingStep}
						/>
					</Card>
				)}

				{modals.delete && (
					<DeleteModal
						walletName={wallet.wallet_name}
						onConfirm={handleDeleteWallet}
						onCancel={() =>
							dispatch({ type: "SET_MODAL", modal: "delete", open: false })
						}
					/>
				)}

				{/* Whitelist Modal */}
				<Dialog
					open={modals.whitelist}
					onOpenChange={(open) =>
						dispatch({ type: "SET_MODAL", modal: "whitelist", open })
					}
				>
					<DialogContent className="sm:max-w-[420px] rounded-xl">
						<DialogHeader>
							<div className="flex items-center gap-3">
								<CheckCircle size={28} className="shrink-0 text-[#00E6C3]" />
								<DialogTitle className="text-(--color-foreground)">
									Whitelist Wallet
								</DialogTitle>
							</div>
						</DialogHeader>
						<div className="py-2">
							<p className="mb-4 text-(--color-muted-foreground) text-sm">
								Select the environment(s) where{" "}
								<strong className="text-(--color-foreground)">
									{wallet.wallet_name}
								</strong>{" "}
								has been whitelisted:
							</p>
							<div className="flex flex-col gap-3">
								{(["Sandbox", "Preview"] as const).map((env) => (
									<label
										key={env}
										className="flex cursor-pointer items-center gap-3"
									>
										<Checkbox
											id={`whitelist-env-${env}`}
											checked={forms.whitelistEnvs.includes(env)}
											onCheckedChange={() =>
												dispatch({
													type: "TOGGLE_WHITELIST_ENV",
													env,
												})
											}
										/>
										<span className="font-medium text-sm text-(--color-foreground)">
											{env}
										</span>
									</label>
								))}
							</div>
							{forms.whitelistEnvs.length === 0 && (
								<p className="mt-3 text-(--color-accent) text-sm">
									Please select at least one environment
								</p>
							)}
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={() =>
									dispatch({
										type: "SET_MODAL",
										modal: "whitelist",
										open: false,
									})
								}
								className="rounded-xl border-(--color-border) hover:bg-(--color-muted)"
							>
								Cancel
							</Button>
							<Button
								onClick={handleWhitelistConfirm}
								disabled={forms.whitelistEnvs.length === 0 || isUpdating}
								className="rounded-xl bg-(--color-cta-primary) hover:bg-(--color-cta-primary-hover)"
							>
								{isUpdating ? "Saving…" : "Confirm Whitelist"}
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>

				{/* Production Whitelisting Info Modal */}
				<Dialog
					open={modals.productionInfo}
					onOpenChange={(open) =>
						dispatch({ type: "SET_MODAL", modal: "productionInfo", open })
					}
				>
					<DialogContent className="sm:max-w-[520px] rounded-xl">
						<DialogHeader>
							<DialogTitle className="text-(--color-foreground)">
								Production Whitelisting Information
							</DialogTitle>
							<DialogDescription className="text-(--color-muted-foreground)">
								Use this information to create a ServiceNow ITSR ticket
							</DialogDescription>
						</DialogHeader>
						<div className="space-y-3 py-2">
							<div className="flex flex-col gap-0.5">
								<span className={infoLabel}>Wallet Name</span>
								<span className={infoValue}>{wallet.wallet_name}</span>
							</div>
							<div className="flex flex-col gap-0.5">
								<span className={infoLabel}>Agent ID</span>
								<span className={infoValue}>
									{wallet.agent_id || "Not set"}
								</span>
							</div>
							<div className="flex flex-col gap-0.5">
								<span className={infoLabel}>Main Office ID</span>
								<span className={infoValue}>
									{wallet.main_office_id || "Not set"}
								</span>
							</div>
							<div className="flex flex-col gap-0.5">
								<span className={infoLabel}>Custodial Type</span>
								<span className={infoValue}>
									{wallet.production_custodial_type || wallet.custodial_type}
								</span>
							</div>
							{wallet.production_toml_file_domain && (
								<div className="flex flex-col gap-0.5">
									<span className={infoLabel}>TOML File Domain</span>
									<span className={infoValue}>
										{wallet.production_toml_file_domain}
									</span>
								</div>
							)}
							{wallet.production_home_domain && (
								<div className="flex flex-col gap-0.5">
									<span className={infoLabel}>Home Domain</span>
									<span className={infoValue}>
										{wallet.production_home_domain}
									</span>
								</div>
							)}
							{wallet.production_funding_address && (
								<div className="flex flex-col gap-0.5">
									<span className={infoLabel}>Funding Address</span>
									<span className={cn(infoValue, "break-all")}>
										{wallet.production_funding_address}
									</span>
								</div>
							)}
							{wallet.production_deposit_address && (
								<div className="flex flex-col gap-0.5">
									<span className={infoLabel}>Deposit Address</span>
									<span className={cn(infoValue, "break-all")}>
										{wallet.production_deposit_address}
									</span>
								</div>
							)}
							{wallet.production_auth_address && (
								<div className="flex flex-col gap-0.5">
									<span className={infoLabel}>Authentication Address</span>
									<span className={cn(infoValue, "break-all")}>
										{wallet.production_auth_address}
									</span>
								</div>
							)}
							{wallet.excluded_countries &&
								wallet.excluded_countries.length > 0 && (
									<div className="flex flex-col gap-1.5">
										<span className={cn(infoLabel, "block")}>
											Excluded Countries
										</span>
										<div className="flex flex-wrap gap-2">
											{wallet.excluded_countries.map((country) => (
												<Badge key={country} variant="destructive">
													{country}
												</Badge>
											))}
										</div>
									</div>
								)}
						</div>
						<DialogFooter>
							<Button
								variant="outline"
								onClick={copyWhitelistInfo}
								className="rounded-xl border-(--color-border) hover:bg-(--color-muted)"
							>
								<Copy size={16} />
								Copy All Info
							</Button>
							<Button
								onClick={() =>
									dispatch({
										type: "SET_MODAL",
										modal: "productionInfo",
										open: false,
									})
								}
								className="rounded-xl bg-(--color-cta-primary) hover:bg-(--color-cta-primary-hover)"
							>
								Close
							</Button>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			</div>
		</div>
	);
};

export default WalletDetail;
